import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, ShieldCheck, KeyRound, MailCheck, Save, AlertTriangle, RefreshCw, Info, Upload, FileCheck2, Trash2, Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import ReadabilityBadge, { ScanQualityTip } from "@/components/ReadabilityBadge";
import { BENEFICIARY_LOCALES, bt } from "@/locales/beneficiaryReview";
import api from "@/lib/api";

/**
 * Beneficiary Secure Review page — a scoped, token-gated public page where
 * a beneficiary reviews and corrects their own case data.
 *
 * Flow: lookup → verify DOB → verify OTP (emailed) → edit → submit.
 * Route: /beneficiary-review/:token (public — token + DOB + OTP act as auth)
 */

const LABELS = {
  beneficiary_first_name: "First name",
  beneficiary_middle_name: "Middle name",
  beneficiary_last_name: "Last name",
  beneficiary_other_names: "Other names used (maiden, aliases)",
  beneficiary_dob: "Date of birth (YYYY-MM-DD)",
  beneficiary_place_of_birth: "Place of birth (city, country)",
  beneficiary_nationality: "Nationality / country of citizenship",
  beneficiary_marital_status: "Marital status",
  beneficiary_address: "Current physical address",
  beneficiary_mailing_address: "Mailing address (if different)",
  beneficiary_phone: "Phone number",
  beneficiary_email: "Email address",
  beneficiary_currently_in_us: "Currently in the U.S.? (yes/no)",
  beneficiary_alien_number: "Alien registration number (A-number, if any)",
  beneficiary_passport_number: "Passport number",
  beneficiary_passport_country: "Passport issuing country",
  beneficiary_passport_expiration: "Passport expiration (YYYY-MM-DD)",
  beneficiary_sevis_number: "SEVIS number (if student/exchange)",
  beneficiary_i94_number: "I-94 arrival record number",
  beneficiary_last_entry_date: "Last entry date to U.S. (YYYY-MM-DD)",
  beneficiary_last_entry_class: "Class of admission on last entry",
  beneficiary_visa_status: "Current visa status",
  ds260_intended_us_address: "Intended U.S. address (where you'll live after approval)",
  ds260_us_contact_relationship: "U.S. contact's relationship to you",
  beneficiary_notes_to_petitioner: "Notes for the sender (anything you want them to know)",
};

const TEXTAREA_KEYS = new Set([
  "beneficiary_address",
  "beneficiary_mailing_address",
  "ds260_intended_us_address",
  "beneficiary_notes_to_petitioner",
]);

function ErrorScreen({ fatalError }) {
  return (
    <AppShell showNav={false}>
      <Centered>
        <div className="rounded-2xl border p-8 max-w-md text-center" style={{borderColor:"var(--border)",background:"var(--bg-card)"}} data-testid="benef-review-error">
          <AlertTriangle className="mx-auto mb-3" size={36} color="#C85A32" />
          <h1 className="font-display text-2xl font-bold mb-2">Link not valid</h1>
          <p className="text-sm" style={{color:"var(--text-secondary)"}}>{fatalError}</p>
          <p className="text-xs mt-6" style={{color:"var(--text-secondary)"}}>
            If you think this is a mistake, contact the person who sent you this link and ask them to send a fresh one.
          </p>
        </div>
      </Centered>
    </AppShell>
  );
}

function SubmittedScreen({ locale }) {
  return (
    <AppShell showNav={false}>
      <Centered>
        <div className="rounded-2xl border p-8 max-w-md text-center" style={{borderColor:"var(--border)",background:"var(--bg-card)"}} data-testid="benef-review-submitted">
          <ShieldCheck className="mx-auto mb-3" size={40} color="#7A9E7E" />
          <h1 className="font-display text-2xl font-bold mb-2">{bt("submitted_title", locale)}</h1>
          <p className="text-sm" style={{color:"var(--text-secondary)"}}>
            {bt("submitted_body", locale)}
          </p>
        </div>
      </Centered>
    </AppShell>
  );
}

function ReviewHeader({ stage, locale, lookup, otpEmailMasked, onLocaleChange }) {
  return (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
             style={{background:"#FDF8F0",color:"#8A6B4F"}}>
          <ShieldCheck size={14} /> {bt("secure_review_badge", locale)}
        </div>
        <LanguagePicker locale={locale} onChange={onLocaleChange} />
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{color:"var(--text-primary)"}}>
        {stage === "dob" && bt("hi_verify_title", locale, lookup?.beneficiary_name || "there")}
        {stage === "otp" && bt("otp_title", locale)}
        {stage === "edit" && bt("edit_title", locale, lookup?.beneficiary_name || "there")}
      </h1>
      <p className="text-sm mt-2" style={{color:"var(--text-secondary)"}}>
        {stage === "dob" && bt("intro_dob", locale, lookup?.sender_role || "petitioner")}
        {stage === "otp" && bt("intro_otp", locale, otpEmailMasked || "your email")}
        {stage === "edit" && bt("intro_edit", locale)}
      </p>
      {lookup?.personal_message && stage === "dob" && (
        <div className="mt-4 rounded-lg border-l-4 p-4"
             style={{borderColor:"#C85A32",background:"#FDF8F0"}} data-testid="benef-personal-message">
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{color:"#8A6B4F"}}>{bt("message_from_sender", locale)}</div>
          <div className="text-sm italic" style={{color:"var(--text-primary)"}}>&ldquo;{lookup.personal_message}&rdquo;</div>
        </div>
      )}
    </div>
  );
}

function DobForm({ dob, setDob, busy, onSubmit, locale }) {
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border p-6" style={{borderColor:"var(--border)",background:"var(--bg-card)"}}>
      <label className="block text-sm font-medium mb-2" style={{color:"var(--text-primary)"}}>
        {bt("label_dob", locale)}
      </label>
      <input type="text" value={dob} onChange={(e) => setDob(e.target.value)}
             placeholder="YYYY-MM-DD or MM/DD/YYYY"
             className="w-full rounded-lg border px-4 py-3 outline-none focus:ring-2"
             style={{borderColor:"var(--border)",background:"var(--bg-input)",color:"var(--text-primary)"}}
             autoFocus data-testid="benef-dob-input" />
      <p className="text-xs mt-2" style={{color:"var(--text-secondary)"}}>
        {bt("hint_dob", locale)}
      </p>
      <button type="submit" disabled={busy}
              className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-white disabled:opacity-60"
              style={{background:"#C85A32"}} data-testid="benef-dob-submit">
        <KeyRound size={16} /> {busy ? "…" : bt("btn_continue", locale)}
      </button>
    </form>
  );
}

function OtpForm({ otp, setOtp, busy, onSubmit, onResend, emailMasked, deliveryInfo, locale }) {
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border p-6" style={{borderColor:"var(--border)",background:"var(--bg-card)"}}>
      <label className="block text-sm font-medium mb-2" style={{color:"var(--text-primary)"}}>
        {bt("label_otp", locale)}
      </label>
      <input type="text" inputMode="numeric" value={otp}
             onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
             placeholder="123456" maxLength={6}
             className="w-full rounded-lg border px-4 py-3 outline-none focus:ring-2 font-mono text-2xl tracking-[0.4em] text-center"
             style={{borderColor:"var(--border)",background:"var(--bg-input)",color:"var(--text-primary)"}}
             autoFocus data-testid="benef-otp-input" />
      <p className="text-xs mt-2" style={{color:"var(--text-secondary)"}}>
        Check the inbox at <strong>{emailMasked}</strong>. Code expires in 10 minutes.
      </p>
      {deliveryInfo && deliveryInfo.delivered === false && (
        <div className="mt-3 rounded-lg p-3 text-xs flex items-start gap-2"
             style={{background:"#FDF8F0",color:"#8A6B4F"}} data-testid="benef-otp-delivery-warning">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>Our email service didn't deliver just now. Try &ldquo;Send a new code&rdquo;, or ask the sender to email you the code directly.</span>
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button type="submit" disabled={busy}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-white disabled:opacity-60"
                style={{background:"#C85A32"}} data-testid="benef-otp-submit">
          <MailCheck size={16} /> {busy ? "…" : bt("btn_verify_code", locale)}
        </button>
        <button type="button" onClick={onResend}
                className="inline-flex items-center gap-2 text-sm font-medium underline"
                style={{color:"var(--text-secondary)"}} data-testid="benef-otp-resend">
          <RefreshCw size={14} /> {bt("btn_send_new_code", locale)}
        </button>
      </div>
    </form>
  );
}

function EditFieldsList({ fields, data, dirty, setField }) {
  return (
    <div className="rounded-2xl border" style={{borderColor:"var(--border)",background:"var(--bg-card)"}}>
      {fields.map((k) => {
        const isTextarea = TEXTAREA_KEYS.has(k);
        const label = LABELS[k] || k;
        const inputProps = {
          value: data[k] || "",
          onChange: (e) => setField(k, e.target.value),
          className: "w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 text-sm",
          style: {borderColor:"var(--border)",background:"var(--bg-input)",color:"var(--text-primary)"},
          "data-testid": `benef-field-${k}`,
        };
        return (
          <div key={k} className="p-4 border-b last:border-b-0" style={{borderColor:"var(--border)"}}>
            <label className="block text-xs font-medium mb-1.5" style={{color:"var(--text-secondary)"}}>
              {label}{dirty[k] && <span className="ml-2 text-[10px]" style={{color:"#C85A32"}}>• unsaved</span>}
            </label>
            {isTextarea ? <textarea rows={3} {...inputProps} /> : <input type="text" {...inputProps} />}
          </div>
        );
      })}
    </div>
  );
}

function SaveBar({ dirtyCount, lastSavedAt, saveBusy, submitBusy, onSave, onSubmit, locale }) {
  return (
    <div className="sticky bottom-4 z-10 rounded-2xl border p-4 flex items-center justify-between shadow-lg"
         style={{borderColor:"var(--border)",background:"var(--bg-card)"}}>
      <div className="text-xs" style={{color:"var(--text-secondary)"}}>
        {saveStatusLabel(dirtyCount, lastSavedAt)}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onSave} disabled={saveBusy || !dirtyCount}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border disabled:opacity-50"
                style={{borderColor:"var(--border)",color:"var(--text-primary)",background:"var(--bg-input)"}}
                data-testid="benef-save-btn">
          <Save size={14} /> {saveBusy ? "…" : bt("btn_save", locale)}
        </button>
        <button onClick={onSubmit} disabled={submitBusy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{background:"#C85A32"}}
                data-testid="benef-submit-btn">
          <Send size={14} /> {submitBusy ? "…" : bt("btn_submit_review", locale)}
        </button>
      </div>
    </div>
  );
}

export default function BeneficiaryReviewPage() {
  const { token } = useParams();
  const [locale, setLocale] = useState(() => {
    try { return localStorage.getItem("benef_review_locale") || "en"; }
    catch { return "en"; }
  });
  const setLocalePersistent = (code) => {
    setLocale(code);
    try { localStorage.setItem("benef_review_locale", code); } catch (err) { console.warn("[BeneficiaryReview] locale not persisted:", err); }
  };
  const [stage, setStage] = useState("loading"); // loading | dob | otp | edit | submitted | error
  const [lookup, setLookup] = useState(null);
  const [fatalError, setFatalError] = useState("");

  // DOB stage
  const [dob, setDob] = useState("");
  const [dobBusy, setDobBusy] = useState(false);

  // OTP stage
  const [otp, setOtp] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpEmailMasked, setOtpEmailMasked] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState("");
  const [otpDeliveryInfo, setOtpDeliveryInfo] = useState(null);

  // Edit stage
  const [session, setSession] = useState("");
  const [caseInfo, setCaseInfo] = useState(null);
  const [fields, setFields] = useState([]);
  const [data, setData] = useState({});
  const [dirty, setDirty] = useState({});
  const [saveBusy, setSaveBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Uploads sub-section
  const [people, setPeople] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [uploadPersonRef, setUploadPersonRef] = useState("beneficiary");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadDeleteBusy, setUploadDeleteBusy] = useState("");

  useEffect(() => {
    if (!token) { setFatalError("This link is malformed."); setStage("error"); return; }
    api.post("/beneficiary-review/lookup", { token })
      .then(({ data }) => { setLookup(data); setStage("dob"); })
      .catch((err) => {
        console.warn("[BeneficiaryReview] lookup failed:", err);
        setFatalError(err.response?.data?.detail || "This link is not valid.");
        setStage("error");
      });
  }, [token]);

  const submitDob = async (e) => {
    e?.preventDefault?.();
    if (!dob.trim()) { toast.error("Please enter your date of birth."); return; }
    setDobBusy(true);
    try {
      const { data: r } = await api.post("/beneficiary-review/verify-dob", { token, dob: dob.trim() });
      setOtpEmailMasked(r.email_masked || "");
      setOtpExpiresAt(r.otp_expires_at || "");
      setOtpDeliveryInfo(r.email_delivery || null);
      setStage("otp");
      toast.success("We just emailed you a 6-digit code.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setDobBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e?.preventDefault?.();
    const code = otp.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) { toast.error("Please enter the 6-digit code."); return; }
    setOtpBusy(true);
    try {
      const { data: r } = await api.post("/beneficiary-review/verify-otp", { token, otp: code });
      setSession(r.session);
      // Load scoped data + people + uploads in parallel
      const [{ data: d }, { data: p }, { data: u }] = await Promise.all([
        api.get(`/beneficiary-review/data?session=${encodeURIComponent(r.session)}`),
        api.get(`/beneficiary-review/people?session=${encodeURIComponent(r.session)}`),
        api.get(`/beneficiary-review/uploads?session=${encodeURIComponent(r.session)}`),
      ]);
      setCaseInfo(d.case || null);
      setFields(d.editable_fields || []);
      setData(d.data || {});
      setPeople(p.people || []);
      setUploads(u.uploads || []);
      setStage("edit");
    } catch (err) {
      const msg = err.response?.data?.detail || "Verification failed.";
      toast.error(typeof msg === "string" ? msg : "Verification failed.");
    } finally {
      setOtpBusy(false);
    }
  };

  const resendOtp = async () => {
    try {
      const { data: r } = await api.post("/beneficiary-review/resend-otp", { token });
      setOtpExpiresAt(r.otp_expires_at || "");
      toast.success("A new code is on its way to your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send a new code.");
    }
  };

  const setField = (k, v) => {
    setData((prev) => ({ ...prev, [k]: v }));
    setDirty((prev) => ({ ...prev, [k]: true }));
  };

  const dirtyCount = useMemo(() => Object.keys(dirty).length, [dirty]);

  const saveChanges = async () => {
    if (!dirtyCount) { toast.info("No changes to save."); return; }
    setSaveBusy(true);
    try {
      const updates = {};
      Object.keys(dirty).forEach((k) => { updates[k] = data[k] || ""; });
      const { data: r } = await api.patch("/beneficiary-review/data", { session, updates });
      toast.success(`Saved ${r.saved_fields} field${r.saved_fields === 1 ? "" : "s"}.`);
      setDirty({});
      setLastSavedAt(new Date().toISOString());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed. Please try again.");
    } finally {
      setSaveBusy(false);
    }
  };

  const uploadFile = async (fileList) => {
    if (!session) return;
    const file = fileList && fileList[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8 MB). Try compressing it first.");
      return;
    }
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("session", session);
      form.append("person_ref", uploadPersonRef || "beneficiary");
      form.append("file", file);
      const { data: r } = await api.post("/beneficiary-review/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Refresh upload list
      const { data: u } = await api.get(`/beneficiary-review/uploads?session=${encodeURIComponent(session)}`);
      setUploads(u.uploads || []);
      const detected = r.upload?.detected_document_type;
      const readScore = r.upload?.readability?.score;
      const lowQuality = Number.isFinite(readScore) && readScore < 60;
      if (lowQuality) {
        toast.warning(`Uploaded — but the scan is hard to read (quality ${readScore}/100). Consider uploading a clearer copy.`);
      } else {
        toast.success(uploadSuccessMessage(detected, readScore));
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Upload failed.");
    } finally {
      setUploadBusy(false);
    }
  };

  const deleteUpload = async (docId) => {
    if (!window.confirm("Remove this upload?")) return;
    setUploadDeleteBusy(docId);
    try {
      await api.delete(`/beneficiary-review/upload/${docId}?session=${encodeURIComponent(session)}`);
      setUploads((prev) => prev.filter((u) => u.id !== docId));
      toast.success("Removed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove.");
    } finally {
      setUploadDeleteBusy("");
    }
  };

  const submitReview = async () => {
    if (dirtyCount) {
      const proceed = window.confirm(`You have ${dirtyCount} unsaved change(s). Save them first?`);
      if (proceed) { await saveChanges(); }
    }
    setSubmitBusy(true);
    try {
      await api.post("/beneficiary-review/submit", { session });
      setStage("submitted");
      toast.success("Thank you! Your review has been sent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitBusy(false);
    }
  };

  // ---- Rendering ----
  if (stage === "loading") {
    return <AppShell showNav={false}><Centered><p className="text-sm" style={{color:"var(--text-secondary)"}}>Loading…</p></Centered></AppShell>;
  }

  if (stage === "error") {
    return <ErrorScreen fatalError={fatalError} />;
  }

  if (stage === "submitted") {
    return <SubmittedScreen locale={locale} />;
  }

  return (
    <AppShell showNav={false}>
      <div className="mx-auto max-w-2xl px-6 py-12" data-testid={`benef-review-${stage}`}>
        <ReviewHeader
          stage={stage}
          locale={locale}
          lookup={lookup}
          otpEmailMasked={otpEmailMasked}
          onLocaleChange={setLocalePersistent}
        />

        {stage === "dob" && (
          <DobForm dob={dob} setDob={setDob} busy={dobBusy} onSubmit={submitDob} locale={locale} />
        )}

        {stage === "otp" && (
          <OtpForm
            otp={otp}
            setOtp={setOtp}
            busy={otpBusy}
            onSubmit={submitOtp}
            onResend={resendOtp}
            emailMasked={otpEmailMasked}
            deliveryInfo={otpDeliveryInfo}
            locale={locale}
          />
        )}

        {stage === "edit" && (
          <div className="space-y-4">
            <div className="rounded-lg p-3 text-xs flex items-start gap-2"
                 style={{background:"#EEF3EA",color:"#4B6A4F"}}>
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              <span>{bt("hint_scope", locale)}</span>
            </div>

            <EditFieldsList fields={fields} data={data} dirty={dirty} setField={setField} />

            <UploadsSection
              people={people}
              uploads={uploads}
              uploadPersonRef={uploadPersonRef}
              setUploadPersonRef={setUploadPersonRef}
              onUpload={uploadFile}
              onDelete={deleteUpload}
              busy={uploadBusy}
              deleteBusy={uploadDeleteBusy}
            />

            <SaveBar
              dirtyCount={dirtyCount}
              lastSavedAt={lastSavedAt}
              saveBusy={saveBusy}
              submitBusy={submitBusy}
              onSave={saveChanges}
              onSubmit={submitReview}
              locale={locale}
            />
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Centered({ children }) {
  return (
    <div className="mx-auto max-w-md px-6 py-20 flex flex-col items-center justify-center">
      {children}
    </div>
  );
}

/**
 * Compact language picker rendered in the page header — helps non-English-
 * speaking beneficiaries navigate the review flow in their own language.
 */
function LanguagePicker({ locale, onChange }) {
  return (
    <select
      value={locale}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs rounded-lg border px-2 py-1"
      style={{borderColor:"var(--border)",background:"var(--bg-card)",color:"var(--text-primary)"}}
      aria-label="Change language"
      data-testid="benef-language-picker"
      title={bt("language", locale)}
    >
      {BENEFICIARY_LOCALES.map((l) => (
        <option key={l.code} value={l.code}>
          {l.flag} {l.nativeName}
        </option>
      ))}
    </select>
  );
}

const DOC_LABELS = {
  passport: "passport",
  birth_certificate: "birth certificate",
  marriage_certificate: "marriage certificate",
  divorce_certificate: "divorce certificate",
  death_certificate: "death certificate",
  adoption_decree: "adoption decree",
  name_change_order: "court name-change order",
  korean_family_registry: "Korean Family Registry",
  japanese_koseki: "Japanese Koseki",
  chinese_hukou: "Chinese Hukou",
  vietnamese_household_registry: "Vietnamese Household Registry",
  foreign_civil_document: "foreign civil document",
  national_id: "national ID",
  drivers_license: "driver's license",
  naturalization_certificate: "naturalization certificate",
  green_card: "green card",
  other: "document",
};

function humanizeDoc(detected) {
  const key = (detected || "").toLowerCase();
  return DOC_LABELS[key] || detected || "document";
}

function uploadSuccessMessage(detected, readScore) {
  if (!detected) return "Uploaded.";
  const quality = Number.isFinite(readScore) ? ` (quality ${readScore}/100)` : "";
  return `Uploaded — detected as ${humanizeDoc(detected)}${quality}.`;
}

function saveStatusLabel(dirtyCount, lastSavedAt) {
  if (dirtyCount > 0) {
    return <span><strong>{dirtyCount}</strong> unsaved change{dirtyCount === 1 ? "" : "s"}</span>;
  }
  if (lastSavedAt) return <span>Saved {new Date(lastSavedAt).toLocaleTimeString()}</span>;
  return <span>No changes yet</span>;
}

function UploadsSection({ people, uploads, uploadPersonRef, setUploadPersonRef, onUpload, onDelete, busy, deleteBusy }) {
  const personLabel = (ref) => {
    const p = people.find((x) => x.person_ref === ref);
    return p ? p.display_name : ref;
  };

  return (
    <section className="rounded-2xl border p-5" style={{borderColor:"var(--border)",background:"var(--bg-card)"}}
             data-testid="benef-uploads-section">
      <div className="flex items-start gap-2 mb-1">
        <Upload size={18} color="#C85A32" className="mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-bold" style={{color:"var(--text-primary)"}}>
            Upload documents
          </h3>
          <p className="text-xs mt-0.5" style={{color:"var(--text-secondary)"}}>
            Add your own docs (passport, birth cert, marriage/divorce cert, family registry).
            You can also add docs for your children, spouse, or parents already on the case.
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-lg p-3 flex flex-col sm:flex-row gap-3 sm:items-center"
           style={{background:"var(--bg-input)"}}>
        <label className="text-xs font-medium shrink-0" style={{color:"var(--text-secondary)"}}>
          Whose document is this?
        </label>
        <select
          value={uploadPersonRef}
          onChange={(e) => setUploadPersonRef(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm flex-1"
          style={{borderColor:"var(--border)",background:"var(--bg-card)",color:"var(--text-primary)"}}
          data-testid="benef-upload-person-select"
        >
          {people.map((p) => (
            <option key={p.person_ref} value={p.person_ref}>
              {p.display_name} — {p.role}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-3"><ScanQualityTip /></div>

      <label className="mt-3 block cursor-pointer rounded-xl border-2 border-dashed p-6 text-center"
             style={{borderColor:"var(--border)",background:"var(--bg-input)"}}
             data-testid="benef-upload-dropzone">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={busy}
          onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }}
          data-testid="benef-upload-input"
        />
        {busy ? (
          <div className="inline-flex items-center gap-2 text-sm" style={{color:"var(--text-secondary)"}}>
            <Loader2 className="animate-spin" size={16} /> Analyzing document…
          </div>
        ) : (
          <div>
            <div className="text-sm font-medium" style={{color:"var(--text-primary)"}}>
              Tap or drop a file to upload for <strong>{personLabel(uploadPersonRef)}</strong>
            </div>
            <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>
              PDF, JPEG, PNG, or WEBP · up to 8 MB. Non-English docs are OK — we translate.
            </div>
          </div>
        )}
      </label>

      {uploads.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="benef-uploads-list">
          {uploads.map((u) => (
            <li key={u.id} className="rounded-lg border p-3"
                style={{borderColor:"var(--border)",background:"var(--bg-input)"}}
                data-testid={`benef-upload-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  <FileCheck2 size={16} color="#4B6A4F" className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" style={{color:"var(--text-primary)"}}>{u.filename}</div>
                    <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap" style={{color:"var(--text-secondary)"}}>
                      <span>{humanizeDoc(u.detected_document_type || u.doc_type)}</span>
                      <span>·</span>
                      <span>for {personLabel(u.person_ref)}</span>
                      {u.detected_language && u.detected_language !== "en" && (
                        <><span>·</span><span>lang: {u.detected_language}</span></>
                      )}
                      {typeof u.confidence === "number" && (
                        <><span>·</span><span>{Math.round(u.confidence * 100)}% confident</span></>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onDelete(u.id)}
                  disabled={deleteBusy === u.id}
                  className="p-1.5 rounded hover:opacity-70 disabled:opacity-40"
                  style={{color:"var(--text-secondary)"}}
                  data-testid={`benef-upload-delete-${u.id}`}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <ReadabilityBadge readability={u.readability} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
